import { useCallback, useEffect, useRef } from 'react';
import { calcFromDate } from '../utils/eodChartMath';
import { isMarketOpen } from '../utils/marketUtils';

const API = '/api/api_idx_chart.php';

function eodFingerprint(rows) {
  if (!rows?.length) return '';
  const last = rows[rows.length - 1];
  return `${rows.length}|${last.date}|${last.close}`;
}

async function fetchEodSeries(apiSymbol, from, to) {
  const url = `${API}?${new URLSearchParams({ action: 'chartdata', idx: apiSymbol, from, to })}`;
  const res = await fetch(url, { credentials: 'include', cache: 'no-store' });
  const json = await res.json();
  if (json.error) throw new Error(json.error);
  if (!json.data?.length) throw new Error('No EOD data');
  return json.data;
}

/**
 * Slow continuous refresh for EOD timeframes (D/W/M).
 * Appends new daily bars as they land without a manual date picker.
 */
export function useLiveBoardEod({
  enabled,
  symbols,
  period,
  setRawBySym,
  onStatusChange,
}) {
  const inflightRef = useRef(false);
  const failCountRef = useRef(0);

  const refreshAll = useCallback(async () => {
    if (!enabled || inflightRef.current) return;
    const eodSymbols = symbols.filter(s => s.api);
    if (!eodSymbols.length) return;

    const { from, to } = calcFromDate(period || '1M');
    inflightRef.current = true;
    // Silent EOD refresh — no Polling badge in the UI

    try {
      const results = await Promise.all(
        eodSymbols.map(async (s) => {
          const data = await fetchEodSeries(s.api, from, to);
          return { id: s.id, data };
        }),
      );

      failCountRef.current = 0;
      setRawBySym(prev => {
        let changed = false;
        const next = { ...prev };
        results.forEach(r => {
          const fp = eodFingerprint(r.data);
          const prevFp = eodFingerprint(prev[r.id]);
          if (fp !== prevFp) {
            next[r.id] = r.data;
            changed = true;
          }
        });
        return changed ? next : prev;
      });

      onStatusChange?.({
        mode: 'historical',
        lastSuccess: Date.now(),
        error: null,
      });
    } catch (err) {
      failCountRef.current += 1;
      onStatusChange?.({
        mode: failCountRef.current >= 5 ? 'offline' : 'historical',
        error: null,
        lastSuccess: null,
      });
    } finally {
      inflightRef.current = false;
    }
  }, [enabled, symbols, period, setRawBySym, onStatusChange]);

  useEffect(() => {
    if (!enabled) {
      onStatusChange?.({ mode: 'idle', error: null });
      return undefined;
    }

    refreshAll();
    const pollMs = isMarketOpen() ? 60000 : 300000;
    const pollId = setInterval(refreshAll, pollMs);
    return () => clearInterval(pollId);
  }, [enabled, refreshAll, onStatusChange]);

  return { refreshAll };
}